import { useState, useCallback } from 'react';
import axios, { isAxiosError } from 'axios';
import { useGlobalStore } from '@digiwedge/global-state';
import { authApi, getTokenDeliveryMode, } from '@digiwedge/idp-client';
import { useAsyncOperation, classifyAxiosError } from './utils/index.js';
import { resolveAudience } from './resolveAudience.js';
import { blockRefresh, isRefreshBlocked, resetRefreshBlock } from './refresh-guard.js';
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function hasTokenTriple(value) {
    if (!isRecord(value))
        return false;
    return (typeof value['accessToken'] === 'string' &&
        typeof value['refreshToken'] === 'string' &&
        typeof value['jti'] === 'string');
}
function isCookieAuthOk(value) {
    return isRecord(value) && value['ok'] === true;
}
async function ensureAuthCsrf() {
    const apiInternals = authApi;
    const base = (apiInternals.basePath ?? '').replace(/\/+$/, '');
    // If a non-empty basePath is configured (cross-origin IDP), treat it as the IDP origin
    // and fetch CSRF from that origin with credentials so the IDP can set its cookie.
    if (base) {
        const target = `${base}/api/auth/csrf`;
        await fetch(target, { method: 'GET', credentials: 'include' });
        return;
    }
    // Otherwise, default to same-origin using axios (works with the dev proxy).
    if (apiInternals.axios) {
        await apiInternals.axios.get('/api/auth/csrf', { withCredentials: true });
        return;
    }
    await fetch('/api/auth/csrf', { method: 'GET', credentials: 'include' });
}
function resolveUserAgent() {
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
        return navigator.userAgent;
    }
    return 'teetime-web/unknown';
}
function readCookie(name) {
    if (typeof document === 'undefined')
        return null;
    const cookie = document.cookie;
    if (!cookie)
        return null;
    const parts = cookie.split('; ');
    for (const part of parts) {
        const [key, ...rest] = part.split('=');
        if (key === name) {
            return decodeURIComponent(rest.join('='));
        }
    }
    return null;
}
/**
 * Hook for user login.
 *
 * @returns { login, loading, error }
 */
export const useLogin = () => {
    const { execute: loginRequest, loading, error, } = useAsyncOperation(async (credentials) => {
        const aud = resolveAudience();
        const api = authApi;
        await ensureAuthCsrf();
        const response = await api.authControllerLogin(credentials, undefined, aud);
        return response.data;
    });
    const storeLogin = useGlobalStore((state) => state.login);
    const setAuthenticated = useGlobalStore((state) => state.setAuthenticated);
    const login = useCallback(async (payload) => {
        const { remember, ...dto } = payload;
        const result = await loginRequest(dto);
        if (!isRecord(result)) {
            throw new Error('Unexpected login response (not JSON object). Check that this app routes `/api/auth/*` to the IDP backend.');
        }
        const cookieOnly = getTokenDeliveryMode() === 'cookie';
        if (cookieOnly) {
            if (hasTokenTriple(result)) {
                storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
            }
            else if (isCookieAuthOk(result)) {
                setAuthenticated();
            }
        }
        else if (hasTokenTriple(result)) {
            storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
        }
        resetRefreshBlock();
        return result;
    }, [loginRequest, setAuthenticated, storeLogin]);
    return { login, loading, error };
};
/**
 * Hook for user registration.
 *
 * @returns { register, loading, error }
 */
export const useRegister = () => {
    const { execute: registerRequest, loading, error, } = useAsyncOperation(async (data) => {
        const response = await authApi.authControllerRegister(data);
        return response.data;
    });
    return { register: registerRequest, loading, error };
};
export const useForgotPasswordRequest = () => {
    const { execute: request, loading, error, } = useAsyncOperation(async ({ email, returnUrl }) => {
        try {
            await ensureAuthCsrf();
            const userAgent = resolveUserAgent();
            const api = authApi;
            await api.authControllerForgotPassword(userAgent, { email, ...(returnUrl ? { returnUrl } : {}) });
        }
        catch (err) {
            if (isAxiosError(err)) {
                const { code, message } = classifyAxiosError(err, 'Unable to send reset email');
                const wrapped = new Error(message);
                wrapped.code = code;
                throw wrapped;
            }
            if (err instanceof Error) {
                throw err;
            }
            throw new Error('Unexpected forgot password error');
        }
    });
    const submit = useCallback(async ({ email, returnUrl }) => {
        const trimmedEmail = email.trim();
        const sanitizedReturn = returnUrl?.trim();
        const payload = { email: trimmedEmail };
        if (sanitizedReturn) {
            payload.returnUrl = sanitizedReturn;
        }
        await request(payload);
    }, [request]);
    return { submit, loading, error };
};
export const useResetPasswordRequest = () => {
    const { execute: request, loading, error, } = useAsyncOperation(async ({ token, password }) => {
        try {
            await ensureAuthCsrf();
            const userAgent = resolveUserAgent();
            const api = authApi;
            await api.authControllerResetPassword(userAgent, { token, password });
        }
        catch (err) {
            if (isAxiosError(err)) {
                const { code, message } = classifyAxiosError(err, 'Unable to reset password');
                const wrapped = new Error(message);
                wrapped.code = code;
                throw wrapped;
            }
            if (err instanceof Error) {
                throw err;
            }
            throw new Error('Unexpected reset password error');
        }
    });
    const submit = useCallback(async ({ token, password }) => {
        const trimmedToken = token.trim();
        await request({ token: trimmedToken, password });
    }, [request]);
    return { submit, loading, error };
};
/**
 * Hook for completing a social registration using a signup token.
 */
export const useRegisterSocial = () => {
    const storeLogin = useGlobalStore((state) => state.login);
    const setAuthenticated = useGlobalStore((state) => state.setAuthenticated);
    const { execute: registerSocialRequest, loading, error, } = useAsyncOperation(async (body) => {
        // Browser cannot set User-Agent header; call endpoint directly without it.
        const { base, axiosInst } = resolveBaseAndAxios();
        const preferRelative = base === '' && typeof window !== 'undefined';
        const prefix = preferRelative ? '' : base.replace(/\/$/, '');
        const target = prefix ? `${prefix}/api/auth/register-social` : '/api/auth/register-social';
        const { data } = await axiosInst.post(target, body, { withCredentials: true });
        return data;
    });
    const registerSocial = useCallback(async ({ remember, ...body }) => {
        const result = await registerSocialRequest(body);
        const cookieOnly = getTokenDeliveryMode() === 'cookie';
        if (!cookieOnly && !hasTokenTriple(result)) {
            throw new Error('Unexpected social register response (missing tokens). Check that this app routes `/api/auth/*` to the IDP backend.');
        }
        if (cookieOnly) {
            if (hasTokenTriple(result)) {
                storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
            }
            else if (isCookieAuthOk(result)) {
                setAuthenticated();
            }
        }
        else if (hasTokenTriple(result)) {
            storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
        }
        return result;
    }, [registerSocialRequest, setAuthenticated, storeLogin]);
    return { registerSocial, loading, error };
};
/**
 * Hook for fetching the user's profile.
 *
 * @returns { profile, fetchProfile, loading, error }
 */
export const useProfile = () => {
    const [profile, setProfile] = useState(null);
    const fetchProfileApi = useCallback(async () => {
        const response = await authApi.authControllerGetProfile();
        return response.data;
    }, []);
    const { execute: fetchProfile, loading, error } = useAsyncOperation(fetchProfileApi);
    const loadProfile = useCallback(async () => {
        const userProfile = await fetchProfile();
        setProfile(userProfile);
        return userProfile;
    }, [fetchProfile]);
    return { profile, fetchProfile: loadProfile, loading, error };
};
// hooks-auth.ts
export const useLogout = () => {
    const storeLogout = useGlobalStore((state) => state.logout);
    const logoutApi = useCallback(async () => {
        const { token } = useGlobalStore.getState();
        console.debug('Current token before logout', {
            token,
        });
        await authApi.authControllerLogout();
        // After the API call succeeds, clear tokens in your global store
        storeLogout();
    }, [storeLogout]);
    const { execute: logout, loading, error } = useAsyncOperation(logoutApi);
    return { logout, loading, error };
};
/**
 * Hook to refresh JWT access token using a refresh token.
 *
 * @returns { refresh, loading, error }
 */
export const useRefresh = () => {
    const { execute: refreshRequest, loading, error, } = useAsyncOperation(async (refreshDto) => {
        if (isRefreshBlocked()) {
            throw new Error('Refresh disabled until user re-authenticates.');
        }
        // Avoid forbidden "User-Agent" header in browsers by calling the endpoint directly.
        const internals = authApi;
        const base = (internals.basePath ?? '').replace(/\/$/, '');
        const target = base ? `${base}/api/auth/refresh` : '/api/auth/refresh';
        const audience = resolveAudience();
        let xsrf = readCookie('XSRF-TOKEN');
        if (!xsrf) {
            await ensureAuthCsrf();
            xsrf = readCookie('XSRF-TOKEN');
        }
        const headers = {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-Client-Audience': audience,
        };
        if (xsrf)
            headers['X-XSRF-Token'] = xsrf;
        if (getTokenDeliveryMode() === 'cookie') {
            headers['X-Token-Delivery'] = 'cookie';
        }
        const res = await fetch(target, {
            method: 'POST',
            credentials: 'include',
            headers,
            cache: 'no-store',
            body: JSON.stringify(refreshDto),
        });
        if (!res.ok) {
            if (res.status === 401)
                blockRefresh();
            throw new Error(`Refresh failed: ${res.status}`);
        }
        return (await res.json());
    });
    const storeLogin = useGlobalStore((state) => state.login);
    const setAuthenticated = useGlobalStore((state) => state.setAuthenticated);
    const refresh = useCallback(async (refreshDto, remember) => {
        const result = await refreshRequest(refreshDto);
        const cookieOnly = getTokenDeliveryMode() === 'cookie';
        if (cookieOnly) {
            if (hasTokenTriple(result)) {
                storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
            }
            else if (isCookieAuthOk(result)) {
                setAuthenticated();
            }
        }
        else if (hasTokenTriple(result)) {
            storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
        }
        return result;
    }, [refreshRequest, setAuthenticated, storeLogin]);
    return { refresh, loading, error };
};
/**
 * Hook to verify MFA during login (unauthenticated flow).
 * Calls `/api/auth/mfa/verify-login` and stores returned tokens.
 */
export const useVerifyLoginMfa = () => {
    const storeLogin = useGlobalStore((state) => state.login);
    const setAuthenticated = useGlobalStore((state) => state.setAuthenticated);
    const { execute: verifyRequest, loading, error, } = useAsyncOperation(async (dto) => {
        const aud = resolveAudience();
        const api = authApi;
        const response = await api.authControllerVerifyLoginMfa(dto, undefined, aud);
        return response.data;
    });
    const verifyLoginMfa = useCallback(async (dto) => {
        const { remember, ...payload } = dto;
        const result = await verifyRequest(payload);
        const cookieOnly = getTokenDeliveryMode() === 'cookie';
        if (!cookieOnly && !hasTokenTriple(result)) {
            throw new Error('Unexpected MFA verification response (missing tokens). Check that this app routes `/api/auth/*` to the IDP backend.');
        }
        if (cookieOnly) {
            if (hasTokenTriple(result)) {
                storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
            }
            else if (isCookieAuthOk(result)) {
                setAuthenticated();
            }
        }
        else if (hasTokenTriple(result)) {
            storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
        }
        return result;
    }, [verifyRequest, setAuthenticated, storeLogin]);
    return { verifyLoginMfa, loading, error };
};
// ---- Social provider helpers (local) ----
function resolveBaseAndAxios() {
    const apiInternals = authApi;
    const base = apiInternals.basePath ?? '';
    const axiosInst = apiInternals.axios || axios;
    return { base, axiosInst };
}
async function sdkListLinkedProviders() {
    const { base, axiosInst } = resolveBaseAndAxios();
    const preferRelative = base === '' && typeof window !== 'undefined';
    const prefix = preferRelative ? '' : base.replace(/\/$/, '');
    const target = prefix ? `${prefix}/api/auth/social/providers` : '/api/auth/social/providers';
    const { data } = await axiosInst.get(target);
    return data;
}
async function sdkUnlinkSocialProvider(provider) {
    const { base, axiosInst } = resolveBaseAndAxios();
    const preferRelative = base === '' && typeof window !== 'undefined';
    const prefix = preferRelative ? '' : base.replace(/\/$/, '');
    const target = prefix ? `${prefix}/api/auth/social/unlink` : '/api/auth/social/unlink';
    const { data } = await axiosInst.post(target, { provider });
    return data;
}
/**
 * Hook to list linked social providers for the current user.
 */
export const useListLinkedProviders = () => {
    const { execute: fetchProviders, loading, error, } = useAsyncOperation(async () => {
        return sdkListLinkedProviders();
    });
    return { listLinkedProviders: fetchProviders, loading, error };
};
/**
 * Hook to unlink a social provider for the current user.
 */
export const useUnlinkSocialProvider = () => {
    const { execute: unlink, loading, error, } = useAsyncOperation(async (provider) => {
        return sdkUnlinkSocialProvider(provider);
    });
    return { unlinkSocialProvider: unlink, loading, error };
};
//# sourceMappingURL=hooks-auth.js.map